/**
 * Manual and suppressed storefront tags (Phase C4) — the one place a listing's pushed tags are
 * assembled from its system tags and what managers changed.
 *
 *   effective = system tags − suppressed + manual
 *
 * System tags are what the push has always computed (game, set, edition, the registry category's
 * tags, "Pre-order", "New Arrival" — see PlatformSyncService.listingTagsForGroup). They exist to
 * satisfy the store's smart-collection rules, which are written against SEPARATE tags ("Booster"
 * + "Box"), so they stay computed and are only ever shown read-only.
 *
 * With no manual and no suppressed tags `effectiveListingTags` returns the system tags unchanged,
 * in order — the parity test (listing-tags.golden.json) holds the push to that.
 *
 * Shopify compares tags case-insensitively and stores at most 250 per product, and a comma is its
 * tag separator. Manual tags are therefore de-duplicated case-insensitively (first spelling wins)
 * but NOT punctuation-insensitively the way system tags are: "Magic: The Gathering" next to the
 * system's "Magic The Gathering" is a different Shopify tag a collection may need.
 *
 * One file, two copies: backend/src/utils/product-tags.ts and
 * frontend/src/components/products/productDrawer/productTagRules.ts are identical (the drawer
 * previews the pushed tags as the manager types), and the frontend test fails if they differ.
 * Edit one, copy it over. Keep this module dependency-free.
 */

export const SHOPIFY_TAG_LIMIT = 250
export const TAG_MAX_LENGTH = 255

/** A system tag and why the product has it ("category: Booster Box", "game", "pre-order"…). */
export interface SourcedTag {
  tag: string
  source: string
}

/** How Shopify tells two tags apart: case-insensitively. */
export function tagMatchKey(tag: string): string {
  return tag.trim().replace(/\s+/g, ' ').toLowerCase()
}

/**
 * One tag as stored: trimmed, inner whitespace collapsed to single spaces. Null when nothing is
 * left, when it holds a comma (Shopify would split it into two tags) or is over 255 characters.
 */
export function normaliseTag(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const tag = raw.trim().replace(/\s+/g, ' ')
  if (!tag || tag.includes(',') || tag.length > TAG_MAX_LENGTH) return null
  return tag
}

/** Why a typed tag is refused, or null when it is fine. For validators and the UI. */
export function tagProblem(raw: string): string | null {
  const tag = raw.trim().replace(/\s+/g, ' ')
  if (!tag) return 'A tag cannot be empty'
  if (tag.includes(',')) return 'A tag cannot contain a comma (Shopify reads it as two tags)'
  if (tag.length > TAG_MAX_LENGTH) return `A tag can be at most ${TAG_MAX_LENGTH} characters`
  return null
}

/** Normalised, case-insensitively de-duplicated (first spelling kept), at most `limit` long. */
export function normaliseTagList(tags: readonly unknown[] | null | undefined, limit = SHOPIFY_TAG_LIMIT): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of tags ?? []) {
    const tag = normaliseTag(raw)
    if (!tag) continue
    const key = tagMatchKey(tag)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(tag)
    if (out.length >= limit) break
  }
  return out
}

/** Same tags, whatever the order and case. */
export function sameTagSet(a: readonly string[], b: readonly string[]): boolean {
  const keysA = new Set(normaliseTagList(a).map(tagMatchKey))
  const keysB = new Set(normaliseTagList(b).map(tagMatchKey))
  if (keysA.size !== keysB.size) return false
  for (const key of keysA) if (!keysB.has(key)) return false
  return true
}

/** Same tags in the same order and spelling — what decides whether a listing needs a re-push. */
export function sameTagList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((tag, i) => tag === b[i])
}

/**
 * The pushed tags: system tags in their computed order minus the suppressed ones, then the manual
 * tags not already present, capped at Shopify's 250 (system tags first, so a full manual list can
 * never push a collection tag off the end).
 */
export function effectiveListingTags(input: {
  system: readonly SourcedTag[]
  manual?: readonly string[] | null
  suppressed?: readonly string[] | null
}): string[] {
  const suppressed = new Set(normaliseTagList(input.suppressed).map(tagMatchKey))
  const out: string[] = []
  const seen = new Set<string>()
  for (const { tag } of input.system) {
    const key = tagMatchKey(tag)
    if (suppressed.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  for (const tag of normaliseTagList(input.manual)) {
    const key = tagMatchKey(tag)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  return out.slice(0, SHOPIFY_TAG_LIMIT)
}

/**
 * Several product rows share one Shopify listing (every printing and condition of a card). Their
 * row-level tags combine as:
 *
 * - manual: the UNION — a tag any row carries is on the listing;
 * - suppressed: the INTERSECTION — a system tag leaves the listing only when every row suppresses
 *   it. With a union, one stray row (or a restocked sibling that inherited an old value) could
 *   drop a collection tag such as "Booster" for the whole listing with nobody looking at it; with
 *   the intersection the worst case is that a suppression does not take effect, which is visible
 *   in the drawer and harmless to the storefront.
 *
 * The tag editor writes the same lists to every row of a listing, so rows normally agree; the rule
 * decides what happens when they don't (a row added later starts empty and so un-suppresses).
 * Rows are taken in the order given, so the first row's spelling wins.
 */
export function combineListingRowTags(rows: ReadonlyArray<{ manualTags?: readonly string[] | null; suppressedTags?: readonly string[] | null }>): {
  manual: string[]
  suppressed: string[]
} {
  const manual = normaliseTagList(rows.flatMap((row) => row.manualTags ?? []))
  if (rows.length === 0) return { manual, suppressed: [] }
  const [first, ...rest] = rows
  const restKeys = rest.map((row) => new Set(normaliseTagList(row.suppressedTags).map(tagMatchKey)))
  const suppressed = normaliseTagList(first.suppressedTags).filter((tag) => restKeys.every((keys) => keys.has(tagMatchKey(tag))))
  return { manual, suppressed }
}
